import { Skill } from "../../models/skill";
import { SkillTrainerState } from "./skillTrainerSlice";
import { groupBy, uniq, isEqual } from 'lodash';
import { skillTrainerData } from "./skillTrainerData";
import { permute } from "../../services/util";

const rates = skillTrainerData.getRates();
const pracGains = skillTrainerData.getPracGainsByRace();
const pracCosts = skillTrainerData.getPracCostsByProfession();

class SkillTrainerCalc {
  getTrainerResults(state: SkillTrainerState): SkillTrainerResult {
    const simpleResults: Skill[] = [];
    for (let skill of state.skills.filter(x => !x.residualGroup)) {
      simpleResults.push(this.calcSimpleSkill(state, skill));
    }

    const residualResults: Skill[][] = [];
    const residualGroups = groupBy(state.skills.filter(x => x.residualGroup), x => x.residualGroup);
    for (let skills of Object.values(residualGroups)) {
      residualResults.push(this.calcResidualSkills(state, skills));
    }
    
    const results = simpleResults.concat(residualResults.flatMap(x => x));
    const commands = this.getCommands(results);
    const { residualOrderIds, residualOrderType} = this.calcResidualOrder(residualResults);
    const pracs = results.reduce((a, b) => a + b.pracsActual, 0);
    const { level, remainderPracs } = this.getLevelRequired(state, pracs);

    return {
      skills: results,
      commands,
      residualOrderIds,
      residualOrderType,
      pracs,
      remainderPracs,
      level
    };
  }

  private calcSimpleSkill(state: SkillTrainerState, skill: Skill): Skill {
    skill = { ...skill } as Skill;
    
    //initialize skill
    skill.percentActual = skill.percentStart;
    skill.pracsActual = 0;
    skill.pracsCount = 0;
    skill.percentHistory = [];
    
    //calculate skill
    const rateKey = this.getRateKey(state, skill);
    const pracCost = this.getPracCost(state, skill);
    
    while (skill.percentActual < skill.percentTarget) {
      skill.percentActual = this.nextPercent(skill.percentActual, skill.weight, rateKey);
      skill.pracsActual += pracCost;
      skill.pracsCount++;
      skill.percentHistory.push(skill.percentActual);
    }

    return skill;
  }

  private calcResidualSkills(state: SkillTrainerState, skills: Skill[]): Skill[] {
    const best = {
      hasValue: false,
      sumPercent: 0,
      sumPracs: 0,
      penalty: 0,
      skills: [] as Skill[]
    };
    
    skills = skills.map(skill => ({ ...skill }) as Skill);
    skills.sort((a, b) => b.percentTarget - a.percentTarget);
    const isRandomized = this.isRandomized(skills);
    const targetedSkills = skills.filter(x => x.percentTarget);
    const untargetedSkills = skills.filter(x => !x.percentTarget);

    for (let pSkills of permute(targetedSkills)) {
      pSkills = pSkills.concat(untargetedSkills);
      
      //initialize skills
      for (let skill of pSkills) {
        skill.percentActual = skill.percentStart;
        skill.pracsActual = 0;
        skill.pracsCount = 0;
        skill.percentHistory = [];
      }
      
      //initialize residuals
      for (let skill of pSkills) {
        if (skill.percentActual) {
          const residual = Math.floor(skill.percentActual * .4);
          for (let otherSkill of pSkills.filter(x => skill.residualIds.includes(x.id))) {
            otherSkill.percentActual = Math.max(otherSkill.percentActual, residual);
          }
        }
      }
      
      //calculate skills
      for (let skill of pSkills) {
        const rateKey = this.getRateKey(state, skill);
        const pracCost = this.getPracCost(state, skill);
        
        let change = false;
        while (skill.percentActual < skill.percentTarget) {
          skill.percentActual = this.nextPercent(skill.percentActual, skill.weight, rateKey);
          skill.pracsActual += pracCost;
          skill.pracsCount++;
          skill.percentHistory.push(skill.percentActual);
          change = true;
        }

        if (change) {
          const residual = Math.floor(skill.percentActual * .4);
          for (let otherSkill of pSkills.filter(x => skill.residualIds.includes(x.id))) {
            otherSkill.percentActual = Math.max(otherSkill.percentActual, residual);
          }
        }
      }

      //penalize gimmicks when randomized
      let penalty = 0;
      if (isRandomized) {
        const badSkill = pSkills.slice(0, -1).find((v, i) => v.percentActual < pSkills[i + 1].percentActual);
        if (badSkill) {
          penalty = 1;
        }
      }

      //capture best result
      const sumPercent = pSkills.reduce((a, b) => a + b.percentActual, 0);
      const sumPracs = pSkills.reduce((a, b) => a + b.pracsActual, 0) + penalty;
      let isBest = !best.hasValue;
      isBest ||= sumPracs < best.sumPracs;
      isBest ||= sumPracs === best.sumPracs && sumPercent > best.sumPercent;
      if (isBest) {
        best.hasValue = true;
        best.sumPercent = sumPercent;
        best.sumPracs = sumPracs;
        best.penalty = penalty;
        best.skills = pSkills.map(skill => ({ ...skill }) as Skill);
      }
    }

    if (best.penalty) {
      console.log('penalized', best.skills.map(x => x.name).join(', '));
    }

    return best.skills;
  }

  private calcResidualOrder(skillSets: Skill[][]) {
    let residualOrderIds: string[] = [];
    let residualOrderType = '';

    const crossSets: Skill[][] = [];
    const crossIdSets: string[][] = [];
    for (let skills of skillSets) {
      skills = skills.filter(x => x.percentTarget);
      if (skills.some(skill => skills.some(x => x.professionId !== skill.professionId))) {
        const ids = skills.map(x => x.professionId);
        crossIdSets.push(ids.filter((v, i) => !i || v !== ids[i - 1]));
        crossSets.push(skills);
      }
    }
    
    if (crossIdSets.length) {
      const professionIds = uniq(crossIdSets.flatMap(x => x));
      const professionResults = permute(professionIds).find(pProfessionIds => {
        return crossIdSets.every(crossIds => {
          const filteredIds = pProfessionIds.filter(x => crossIds.includes(x));
          return isEqual(filteredIds, crossIds);
        });
      });

      if (professionResults) {
        residualOrderIds = professionIds;
        residualOrderType = 'profession';
      } else {
        residualOrderIds = crossSets.flatMap(skill => skill.map(x => x.id));
        residualOrderType = 'skill';
      }
    }
    
    return { residualOrderIds, residualOrderType };
  }

  private nextPercent(percent: number, weight: number, rateKey: number): number {
    if (percent <= 20) {
      percent += Math.floor(weight * rates[rateKey][0]);
    } else if (percent <= 40) {
      percent += Math.floor(weight * rates[rateKey][1]);
    } else if (percent <= 60) {
      percent += Math.floor(weight * rates[rateKey][2]);
    } else if (percent <= 80) {
      percent += Math.floor(weight * rates[rateKey][3]);
    } else if (percent <= 90) {
      percent += Math.floor(weight * rates[rateKey][4]);
    } else {
      percent += 1;
    }
    return percent;
  }

  private getRateKey(state: SkillTrainerState, skill: Skill): number {
    switch (skill.professionId) {
      case 'warrior':
        return Math.floor(state.strn / 2) + Math.floor(state.dext / 4) + Math.floor(state.cont / 4);
      case 'rogue':
        return Math.floor(state.dext * 3 / 4) + Math.floor(state.intl / 4);
      case 'hunter':
        return Math.floor((state.strn + state.intl + state.wilp + state.dext) / 4);
      case 'fade':
        return state.intl;
    }
    return 0;
  }

  private getCommands(skills: Skill[]): { [key: string]: string } {
    const commands: { [key: string]: string } = {
      warrior: '', rogue: '', hunter: '', fade: ''
    };
    for (let result of skills) {
      commands[result.professionId] += `PR ${result.name}\n`.repeat(result.pracsCount);
    }
    for (let key of Object.keys(commands)) {
      commands[key] = commands[key].slice(0, -1);
    }
    return commands;
  }

  private getLevelRequired(state: SkillTrainerState, sumPracs: number) {
    let level = 1;
    let levelPracs = state.bonded ? 18 : 8;
    const pracGain = this.getPracGain(state);
    while (levelPracs < sumPracs) {
      level++;
      levelPracs += level <= 30 ? pracGain : 2;
    };

    let remainderPracs = sumPracs ? levelPracs - sumPracs : 0;
    return { level, levelPracs, remainderPracs };
  }

  private getPracGain(state: SkillTrainerState) {
    return pracGains[state.raceId];
  }

  private getPracCost(state: SkillTrainerState, skill: Skill): number {
    return pracCosts[state.professionId][skill.professionId];
  }

  private isRandomized(skills: Skill[]) {
    return skills.some(x => ['hide', 'camouflage', 'sneak', 'ranger_sneak'].includes(x.id));
  }
}

export const skillTrainerCalc = new SkillTrainerCalc();

export interface SkillTrainerResult {
  skills: Skill[];
  commands: { [key: string]: string };
  residualOrderIds: string[];
  residualOrderType: string;
  pracs: number;
  remainderPracs: number;
  level: number;
}